import type { ReactNode } from "react";
import React from "react";

import "date-fns/locale";
import type { FeaturesResult } from "../AbFeatures/FeaturesProvider";
import FeaturesProvider from "../AbFeatures/FeaturesProvider";
import type { Brands } from "../brand/types";
import { ClientInterfaceProvider } from "../client_interface";
import type { ClientInterface } from "../client_interface/types";
import {
  DeprecatedErrorBoundary,
  DeprecatedErrorHandlingProvider,
} from "../error_handling";
import type { ErrorHandler } from "../error_handling/deprecated/types";
import { FacebookIdProvider } from "../facebookId/FacebookIdProvider";
import type { FlowType } from "../flow";
import { FlowProvider } from "../flow";
import { GoogleClientIdProvider } from "../googleClient/GoogleClientIdProvider";
import { TranslationsProvider } from "../i18n";
import { LegacyNavigationFlowProvider } from "../integration";
import type { FlowActions } from "../integration/types";
import { IsMetaProvider } from "../isMeta/IsMetaProvider";
import { LoggerProvider } from "../logger";
import { MarketingPortalProvider } from "../marketingPortal/MarketingPortalProvider";
import { StorageClientProvider } from "../storage/StorageClientProvider";
import type { StorageClient } from "../storage/types";
import type { TrackingProviderProps } from "../tracking/TrackingProvider";
import { TrackingProvider } from "../tracking/TrackingProvider";
import type { CurrencyFormat } from "../types";
import { WebSiteConfigProvider } from "./WebsiteConfigProvider";

type BaseLog = (message: string) => void;
type ErrorLog = (error: Error, message: string) => void;
type VoidFunction = () => void;

interface LegacyLogger {
  error: ErrorLog;
  info: BaseLog;
  message: BaseLog;
  warn: BaseLog;
  disableLoggers: VoidFunction;
}

interface I18nextOptions {
  parseMissingKeyHandler?: () => "";
}

export interface RouteMatcher {
  getPathParams: (pathname: string) => Record<string, string | Array<string>>;
  findMatchingPattern: (pathname: string) => string | undefined;
}

export interface CurrencyLocaleConfig {
  locale: string;
  minimumFractionDigits?: number;
  numberLocale?: string;
}

export interface AppProviderProps extends TrackingProviderProps {
  errorHandler: ErrorHandler;
  logger: LegacyLogger;
  storageClient: StorageClient;
  i18nextOptions: I18nextOptions;
  flowActions: FlowActions;
  site: string;
  brand: Brands;
  locale: string;
  dateLocale: Locale;
  currencyLocale: CurrencyLocaleConfig;
  currencyFormat?: CurrencyFormat;
  clientInterface: ClientInterface;
  featuresResult: FeaturesResult;
  children: ReactNode;
  context: string;
  flow: FlowType;
  googleClientId: string;
  facebookId: string;
  marketingPortal: string;
  isMeta: boolean;
}

const AppProvider = ({
  errorHandler,
  logger,
  storageClient,
  i18nextOptions,
  flowActions,
  site,
  brand,
  locale,
  dateLocale,
  currencyLocale,
  currencyFormat,
  clientInterface,
  featuresResult,
  pageTracker,
  trackingParamsRepository,
  thirdPartyTrackingAgent,
  customDimensionsRepository,
  datalayerTrackingClient,
  eCommerceTrackingClient,
  dataMeshTrackingClient,
  flow,
  children,
  context,
  googleClientId,
  facebookId,
  marketingPortal,
  isMeta,
}: AppProviderProps) => {
  return (
    <DeprecatedErrorHandlingProvider errorHandler={errorHandler}>
      <DeprecatedErrorBoundary
        onRestore={errorHandler.onRestore}
        stopOnRestore={errorHandler.stopOnRestore}
      >
        <LoggerProvider logger={logger}>
          <StorageClientProvider client={storageClient}>
            <TranslationsProvider options={i18nextOptions}>
              <ClientInterfaceProvider clientInterface={clientInterface}>
                <FlowProvider flow={flow}>
                  <TrackingProvider
                    pageTracker={pageTracker}
                    trackingParamsRepository={trackingParamsRepository}
                    thirdPartyTrackingAgent={thirdPartyTrackingAgent}
                    customDimensionsRepository={customDimensionsRepository}
                    datalayerTrackingClient={datalayerTrackingClient}
                    eCommerceTrackingClient={eCommerceTrackingClient}
                    dataMeshTrackingClient={dataMeshTrackingClient}
                  >
                    <WebSiteConfigProvider
                      site={site}
                      brand={brand}
                      locale={locale}
                      dateLocale={dateLocale}
                      currencyLocale={currencyLocale}
                      currencyFormat={currencyFormat}
                    >
                      <LegacyNavigationFlowProvider {...flowActions}>
                        <FeaturesProvider
                          featuresResult={featuresResult}
                          context={context}
                        >
                          <FacebookIdProvider facebookId={facebookId}>
                            <GoogleClientIdProvider
                              googleClientId={googleClientId}
                            >
                              <MarketingPortalProvider
                                marketingPortal={marketingPortal}
                              >
                                <IsMetaProvider isMeta={isMeta}>
                                  {children}
                                </IsMetaProvider>
                              </MarketingPortalProvider>
                            </GoogleClientIdProvider>
                          </FacebookIdProvider>
                        </FeaturesProvider>
                      </LegacyNavigationFlowProvider>
                    </WebSiteConfigProvider>
                  </TrackingProvider>
                </FlowProvider>
              </ClientInterfaceProvider>
            </TranslationsProvider>
          </StorageClientProvider>
        </LoggerProvider>
      </DeprecatedErrorBoundary>
    </DeprecatedErrorHandlingProvider>
  );
};

export default AppProvider;
